import { INBOUND, OUTBOUND } from 'constants/booking';
import { toDecimalPlaces } from 'utils/calculator';
import { isDbFee } from 'utils/refunds';
import { brandCode } from 'constants/brandCodes';
import { isDBLeg } from 'utils/segmentTypes';

const directions = [OUTBOUND, INBOUND];

const roundAmount = (amount) => toDecimalPlaces(2, amount);

const getBookingItems = (leg = {}) => {
  const { bookingItems = [], products = [] } = leg;
  return bookingItems.length > 0 ? bookingItems : products;
};

const getCancelledProducts = (leg) => getBookingItems(leg).filter((item) => item.cancelled && item.provisional);

const getCreatedProducts = (leg) => getBookingItems(leg).filter((item) => !item.cancelled && item.provisional);

const getCarrierKey = (leg) => leg.serviceType?.code || leg.serviceType?.name || '';

const getCarrierName = (leg) => {
  if (isDBLeg(leg)) return brandCode.DB;

  return leg.serviceType?.brandCode || leg.serviceType?.name || leg.serviceType?.code || '';
};

const groupByCarrier = (items) => {
  const carriers = [];
  const carrierMap = new Map();

  items.forEach(({ carrierKey, carrierName, passengerType, amount }) => {
    if (!carrierMap.has(carrierKey)) {
      carrierMap.set(carrierKey, { key: carrierKey, serviceTypeName: carrierName, total: 0, feeGroups: new Map() });
      carriers.push(carrierMap.get(carrierKey));
    }

    const carrier = carrierMap.get(carrierKey);
    carrier.total += amount;

    if (!passengerType || amount === 0) return;

    const groupKey = `${passengerType}-${amount}`;

    if (!carrier.feeGroups.has(groupKey)) {
      carrier.feeGroups.set(groupKey, { passengerType, amount, quantity: 0 });
    }

    carrier.feeGroups.get(groupKey).quantity += 1;
  });

  return carriers.map((c) => ({
    key: c.key,
    serviceTypeName: c.serviceTypeName,
    total: roundAmount(c.total),
    feeGroups: Array.from(c.feeGroups.values())
  }));
};

const buildDirectionSection = (direction, carriers) => {
  const filtered = carriers.filter((c) => c.total !== 0 || c.feeGroups.length > 0);

  if (filtered.length === 0) return null;

  return {
    direction,
    total: roundAmount(filtered.reduce((sum, c) => sum + c.total, 0)),
    carriers: filtered
  };
};

const extractFeeItems = (legs, direction) => {
  const items = [];

  legs
    .filter((leg) => leg.direction === direction)
    .forEach((leg) => {
      const cancelled = getCancelledProducts(leg);
      if (cancelled.length === 0) return;

      const productMap = new Map(cancelled.filter((p) => p.passengerId).map((p) => [String(p.passengerId), p]));

      const carrierKey = getCarrierKey(leg);
      const carrierName = getCarrierName(leg);

      (leg.fees || [])
        .filter((fee) => fee.provisional && (!fee.passengerId || productMap.has(String(fee.passengerId))) && !isDbFee(fee))
        .forEach((fee) => {
          const product = productMap.get(String(fee.passengerId));
          items.push({
            carrierKey: carrierKey || `${direction}-fallback`,
            carrierName,
            passengerType: product?.passenger?.type,
            amount: fee.price || 0
          });
        });
    });

  return items;
};

const extractFareDiffItems = (cancellationLegs, creationLegs, direction) => {
  const items = [];
  const cancLegs = cancellationLegs.filter((leg) => leg.direction === direction);
  const createLegs = creationLegs.filter((leg) => leg.direction === direction);

  cancLegs.forEach((cancLeg) => {
    const cancelled = getCancelledProducts(cancLeg);
    if (cancelled.length === 0) return;

    const carrierKey = getCarrierKey(cancLeg);
    const creationLeg = createLegs.find((leg) => getCarrierKey(leg) === carrierKey);
    if (!creationLeg) return;

    const createdMap = new Map(
      getCreatedProducts(creationLeg)
        .filter((p) => p.passengerId)
        .map((p) => [String(p.passengerId), p])
    );

    cancelled.forEach((oldProduct) => {
      const newProduct = createdMap.get(String(oldProduct.passengerId));
      if (!newProduct) return;

      const diff = roundAmount((newProduct.price || 0) - (oldProduct.price || 0));
      if (diff === 0) return;

      items.push({
        carrierKey: carrierKey || `${direction}-fallback`,
        carrierName: getCarrierName(cancLeg),
        passengerType: oldProduct.passenger?.type,
        amount: diff
      });
    });
  });

  return items;
};

export const getFeeBreakdown = (pendingCancellationLegs = []) =>
  directions
    .map((direction) => buildDirectionSection(direction, groupByCarrier(extractFeeItems(pendingCancellationLegs, direction))))
    .filter(Boolean);

export const getFareDifferenceBreakdown = (pendingCancellationLegs = [], pendingCreationLegs = []) =>
  directions
    .map((direction) =>
      buildDirectionSection(direction, groupByCarrier(extractFareDiffItems(pendingCancellationLegs, pendingCreationLegs, direction)))
    )
    .filter(Boolean);
